/** @format */

import React, { FC, useState } from "react";
import { Action } from "redux";

import { DeleteOutlined } from "@ant-design/icons";
import { Row, Col, Select } from "antd";
import { showNotification } from "../../../utils/showNotification";
import { API_ENDPOINTS } from "../../../constants/api";
import apiClient from "../../../utils/apiClient";
import UIModal from "../../ui/Modal";
import { UIText } from "../../ui/Text";
import { UITitle } from "../../ui/Title";
import { UIInput } from "../../ui/Input";
import { UICard } from "../../ui/Card";
import { UIButton } from "../../ui/Button";

const { Option } = Select;

export interface TUserDisableReason {
  id: number;
  reason: string;
}

interface Props {
  actionToDispatch: (reasonId: number) => Action | void;
  removeButtonLabel: string;
  title: string;
  text: string;
  "data-cy"?: string;
}

export const DisableUserCard: FC<Props> = ({
  actionToDispatch,
  removeButtonLabel,
  title,
  text,
  "data-cy": dataCy,
}: Props): JSX.Element => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [removeText, setRemoveText] = useState("");
  const [reasonId, setReasonId] = useState<number | undefined>(undefined);
  const [reasons, setReasons] = useState<TUserDisableReason[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);

  const isTextValid = removeText.trim().toLowerCase() === "disabilita";
  const canConfirm = !!reasonId && isTextValid;

    const okButtonStyle = canConfirm
    ? {
        borderRadius: "7px",
        backgroundColor: "#e00102",
        borderColor: "#ac0000",
        }
    : {
        borderRadius: "7px",
        backgroundColor: "#f5f5f5",
        borderColor: "#d9d9d9",
        color: "rgba(0, 0, 0, 0.25)",
    };

  const fetchReasons = (): void => {
    setReasonsLoading(true);
    apiClient
      .get(API_ENDPOINTS.USER_DISABLE_REASONS)
      .then(res => {
        setReasons(res.data.data ?? res.data);
      })
      .catch(() => {
        showNotification(
          "error",
          "Errore",
          "Impossibile recuperare le motivazioni di disabilitazione",
        );
      })
      .finally(() => setReasonsLoading(false));
  };

  const showModal = (): void => {
    setRemoveText("");
    setReasonId(undefined);
    fetchReasons();
    setIsModalVisible(true);
  };

  const handleOk = (): void => {
    if (!canConfirm || !reasonId) return;
    actionToDispatch(reasonId);
    setIsModalVisible(false);
  };

  const handleCancel = (): void => {
    setIsModalVisible(false);
  };

  return (
    <>
      <UIModal
        title={title}
        visible={isModalVisible}
        onOk={handleOk}
        okText="Disabilita"
        okButtonProps={{
          disabled: !canConfirm,
          style: okButtonStyle,
        }}
        cancelButtonProps={{ style: { borderRadius: "7px" } }}
        onCancel={handleCancel}
      >
        <Row style={{ marginBottom: "12px" }}>
          <Col xs={24}>
            <UIText>Motivazione disabilitazione:</UIText>
            <Select<number>
              style={{ width: "100%", marginTop: "4px" }}
              placeholder="Seleziona una motivazione"
              loading={reasonsLoading}
              value={reasonId}
              onChange={(value: number): void => setReasonId(value)}
            >
              {reasons.map(reason => (
                <Option key={reason.id} value={reason.id}>
                  {reason.reason}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <UIText>Per confermare l'operazione digita disabilita:</UIText>
        <UIInput onChange={(e): void => setRemoveText(e.target.value)} />
      </UIModal>
      <UICard>
        <Row>
          <Col>
            <UITitle level={5}>{title}</UITitle>
          </Col>
        </Row>
        <Row justify="space-between" align="middle">
          <Col>
            <UIText>{text}</UIText>
          </Col>
          <Col>
            <UIButton
              onClick={showModal}
              data-cy={dataCy}
              style={{ backgroundColor: "#e00102", borderColor: "transparent", color: "#fff" }}
              hoverBgColor="#da3233"
            >
              <DeleteOutlined />
              {removeButtonLabel}
            </UIButton>
          </Col>
        </Row>
      </UICard>
    </>
  );
};